import {
  getAllQuestionsServerUrl,
  getDeleteFormTemplateServerUrl,
  getFormStructureServerUrl,
  getFormStringsServerUrl,
  getFormTemplateServerUrl,
  getFormsMetadataServerUrl,
  getGeneratePreviewLinkServerUrl,
  getSendOtpServerUrl,
  getUpdateFormTemplateServerUrl,
  getVerifyOtpServerUrl,
  MAX_SEND_OTP_ERROR,
  MAX_VERIFY_OTP_ERROR,
} from '../consts/consts';
import { Translation } from '../consts/translations';
import { FormTemplate, StringsFile, StructureFile } from '../types';
import { InputType } from '../utils/InputType';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { v4 as uuidv4 } from 'uuid';
import { base64EncodeJson } from '../utils/bas64Utils';
import { logClientError } from '../utils/logClientError';

export type MissingTranslation = {
  guid: string;
  question_key?: string;
  slot: string;
  option_guid?: string;
  child_template_id?: string;
  lang: string;
  reason: string;
};

export class SaveValidationError extends Error {
  readonly missingTranslations: MissingTranslation[];

  constructor(missingTranslations: MissingTranslation[]) {
    super('declared language text is missing');
    this.name = 'SaveValidationError';
    this.missingTranslations = missingTranslations;
  }
}

export class SaveConflictError extends Error {
  readonly conflictLangs: string[];

  constructor(conflictLangs: string[]) {
    super('translations changed since the form was loaded');
    this.name = 'SaveConflictError';
    this.conflictLangs = conflictLangs;
  }
}

function isMissingTranslation(value: unknown): value is MissingTranslation {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const row = value as MissingTranslation;
  return (
    typeof row.guid === 'string' && typeof row.slot === 'string' && typeof row.lang === 'string'
  );
}

function cleanPhoneNumber(phoneNumber: string): string {
  let cleanedNumber = phoneNumber.replace(/\D/g, '');

  if (cleanedNumber.startsWith('972')) {
    cleanedNumber = cleanedNumber.slice(3);
  }
  if (!cleanedNumber.startsWith('0')) {
    cleanedNumber = '0' + cleanedNumber;
  }
  return cleanedNumber;
}

async function getClientUniqueIdentifier() {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  } catch (error) {
    logClientError('getClientUniqueIdentifier', error);
    return uuidv4();
  }
}

export const verifyPatientAndSendOtp = async (
  phoneOrEmail: string,
  inputType: InputType,
  sessionId: string,
  translation: Translation,
  language: string,
) => {
  if (inputType === InputType.PhoneNumber) {
    phoneOrEmail = cleanPhoneNumber(phoneOrEmail);
  }
  const uniqueIdentifier = await getClientUniqueIdentifier();
  const response = await fetch(`${getSendOtpServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      phone_or_email: phoneOrEmail,
      input_type: inputType.valueOf(),
      session_id: sessionId,
      website_language: language,
      identifier: uniqueIdentifier,
    }),
  });
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error(MAX_SEND_OTP_ERROR);
    }
    if (response.status === 401) {
      throw new Error(translation.unauthorized_sf_error);
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export const validateCode = async (
  phoneOrEmail: string,
  otp: string,
  inputType: InputType,
  sessionId: string,
  translation: Translation,
  language: string,
) => {
  if (inputType === InputType.PhoneNumber) {
    phoneOrEmail = cleanPhoneNumber(phoneOrEmail);
  }
  const uniqueIdentifier = await getClientUniqueIdentifier();
  const response = await fetch(`${getVerifyOtpServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      phone_or_email: phoneOrEmail,
      otp: otp,
      input_type: inputType.valueOf(),
      session_id: sessionId,
      website_language: language,
      identifier: uniqueIdentifier,
    }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error(translation.invalid_code_message);
    } else if (response.status === 403) {
      throw new Error(MAX_VERIFY_OTP_ERROR);
    } else if (response.status === 404) {
      throw new Error(translation.form_does_not_exist_error);
    } else {
      throw new Error(translation.general_sever_error);
    }
  }
  return await response.json();
};

export const getFormsMetadata = async (translation: Translation, logout: () => void) => {
  const response = await fetch(`${getFormsMetadataServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export const getFormTemplate = async (
  formId: string,
  translation: Translation,
  logout: () => void,
) => {
  const response = await fetch(`${getFormTemplateServerUrl()}/${formId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export const getFormStructure = async (
  formId: string,
  translation: Translation,
  logout: () => void,
): Promise<StructureFile | null> => {
  const response = await fetch(getFormStructureServerUrl(formId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    throw new Error(translation.general_sever_error);
  }
  return (await response.json()) as StructureFile;
};

export const getFormStrings = async (
  formId: string,
  lang: string,
  translation: Translation,
  logout: () => void,
): Promise<StringsFile | null> => {
  const response = await fetch(getFormStringsServerUrl(formId, lang), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    throw new Error(translation.general_sever_error);
  }
  return (await response.json()) as StringsFile;
};

export const updateFormTemplate = async (
  formTemplate: FormTemplate,
  translation: Translation,
  logout: () => void,
  createForm: boolean,
  languages: string[],
  stringsByLang?: Record<string, StringsFile>,
  baseStringsByLang?: Record<string, StringsFile>,
) => {
  const response = await fetch(`${getUpdateFormTemplateServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      form_template_base64: base64EncodeJson(formTemplate),
      ...(stringsByLang && { strings_by_lang_base64: base64EncodeJson(stringsByLang) }),
      ...(baseStringsByLang && {
        base_strings_by_lang_base64: base64EncodeJson(baseStringsByLang),
      }),
      create_form: createForm,
      languages,
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    const body = await response.json().catch(() => null);
    const conflictLangs = body?.conflict_langs;
    if (response.status === 409 && Array.isArray(conflictLangs)) {
      throw new SaveConflictError(conflictLangs.map(String));
    }
    const missing = body?.missing_translations;
    if (Array.isArray(missing) && missing.length > 0 && missing.every(isMissingTranslation)) {
      throw new SaveValidationError(missing);
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export const deleteFormTemplate = async (
  formId: string,
  translation: Translation,
  logout: () => void,
) => {
  const response = await fetch(`${getDeleteFormTemplateServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      form_id: formId,
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

export const getAllQuestions = async (translation: Translation, logout: () => void) => {
  const response = await fetch(`${getAllQuestionsServerUrl()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      logout();
    }
    throw new Error(translation.general_sever_error);
  }
  return await response.json();
};

function previewLinkErrorMessage(translation: Translation, backendMessage?: string): string {
  if (backendMessage?.startsWith('Unsupported language:')) {
    return translation.preview_link_unsupported_language_error;
  }
  if (backendMessage?.startsWith('Form template strings not found for language:')) {
    return translation.preview_link_strings_not_found_error;
  }
  return translation.general_sever_error;
}

export const generatePreviewLink = async (
  formId: string,
  translation: Translation,
  logout: () => void,
  expiryHours?: number,
  language?: string,
): Promise<{ preview_url: string; expires_at: string; language?: string }> => {
  const bodyPayload: { expiry_hours?: number; language?: string } = {};
  if (expiryHours != null) {
    bodyPayload.expiry_hours = expiryHours;
  }
  if (language != null && language.trim()) {
    bodyPayload.language = language;
  }
  const body = Object.keys(bodyPayload).length > 0 ? JSON.stringify(bodyPayload) : undefined;
  const response = await fetch(`${getGeneratePreviewLinkServerUrl()}/${formId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...(body !== undefined && { body }),
  });

  if (response.status === 401 || response.status === 403) {
    logout();
    throw new Error(translation.unauthorized_sf_error);
  }
  if (response.status === 404) {
    throw new Error(translation.form_not_found ?? translation.general_sever_error);
  }
  if (!response.ok) {
    let backendMessage: string | undefined;
    try {
      const errorBody = (await response.json()) as { message?: string };
      backendMessage = errorBody.message;
    } catch {
      // keep default message when error body is not JSON
    }
    throw new Error(previewLinkErrorMessage(translation, backendMessage));
  }
  return await response.json();
};
